import {
  Autorenew,
  DeleteOutlineRounded,
  Edit,
  QrCode,
  Visibility,
} from "@mui/icons-material";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { AxiosError } from "axios";
import { parseISO, format } from "date-fns";
import { useCallback, useState } from "react";
import { useMutation, useQuery } from "react-query";
// import { useNavigate } from "react-router-dom";
import { Table } from "../../components/Table";
import { useDialogConfirmation } from "../../hooks/useDialogConfirmation";
import { useToast } from "../../hooks/useToast";
import { queryClient } from "../../services/queryClient";
import {
  deleteInstance,
  listWhatsAppInstanceService,
  contactsToPeople,
} from "../../services/whatsApp";
import { WhatsAppInstance } from "../../types/whatsApp";
import { ConfigWhatsApp } from "./ConfigInstance";
import { DetailsWhatsApp } from "./Details";
import { NewWhatsApp } from "./New";
import { QrCodeWhatsApp } from "./QrCode";
import { useStyles } from "./styles";

export function WhatsApp() {
  //   const navigate = useNavigate();
  const dialog = useDialogConfirmation();
  const toast = useToast();
  const styles = useStyles();

  const [whatsAppInstance, setWhatsAppInstance] =
    useState<WhatsAppInstance | null>(null);
  const [dialogDetailsWhatsApp, setDialogDetailsWhatsApp] =
    useState<boolean>(false);
  const [dialogConfigWhatsApp, setDialogConfigWhatsApp] =
    useState<boolean>(false);
  const [dialogQrCodeWhatsApp, setDialogQrCodeWhatsApp] =
    useState<boolean>(false);
  const [dialogNewWhatsApp, setDialogNewWhatsApp] = useState<boolean>(false);

  const whatsAppInstances = useQuery(
    "whatsAppInstances",
    listWhatsAppInstanceService
  );

  const instanceContactsToPeople = useMutation(
    async (instance_id: number) => {
      const response = await contactsToPeople(instance_id);
      return response;
    },
    {
      onSuccess: () => {
        toast.show("Contatos convertidos em pessoas com sucesso!", {
          severity: "success",
        });
        dialog.close();
      },
      onError: (err: AxiosError) => {
        const data: any = err.response.data;
        if (data.message === "Sem contatos ativos para converter.") {
          toast.show("Sem contatos ativos para converter", {
            severity: "warning",
          });
        } else if (data.message === "Instância sem contatos.") {
          toast.show("Instância sem contatos", { severity: "warning" });
        } else {
          toast.show("Falha ao converter contatos em pessoas", {
            severity: "error",
          });
        }
      },
    }
  );

  const removeInstance = useMutation(
    async (id: number) => {
      const response = await deleteInstance(id);
      return response;
    },
    {
      onSuccess: () => {
        toast.show("Instância excluída com sucesso!", { severity: "success" });
        queryClient.invalidateQueries("whatsAppInstances");
        dialog.close();
      },
      onError: () => {
        toast.show("Falha ao excluir a instância", { severity: "error" });
      },
    }
  );

  const handleDialogDetailsWhatsApp = useCallback(
    (row: WhatsAppInstance | null) => {
      setWhatsAppInstance(row);
      setDialogDetailsWhatsApp((prev) => !prev);
    },
    []
  );

  const handleDialogConfigWhatsApp = useCallback(
    (row: WhatsAppInstance | null) => {
      setWhatsAppInstance(row);
      setDialogConfigWhatsApp((prev) => !prev);
    },
    []
  );

  const handleDialogQrCodeWhatsApp = useCallback(
    (row: WhatsAppInstance | null) => {
      setWhatsAppInstance(row);
      setDialogQrCodeWhatsApp((prev) => !prev);
    },
    []
  );

  const handleDialogDeleteWhatsApp = useCallback(
    (row: WhatsAppInstance | null) => {
      dialog.show(
        "Aviso",
        "Deseja continuar e apagar a instância e dados agregados permanentemente?",
        {
          disabled: removeInstance.isLoading,
          onConfirmate: async () => {
            await removeInstance.mutateAsync(row.id);
          },
        }
      );
    },
    [removeInstance, dialog]
  );

  const handleConvertContactsToPeople = useCallback(
    (row: WhatsAppInstance | null) => {
      dialog.show("Aviso", "Deseja converter contatos em pessoas?", {
        disabled: instanceContactsToPeople.isLoading,
        onConfirmate: async () => {
          await instanceContactsToPeople.mutateAsync(row.id);
        },
      });
    },
    [dialog, instanceContactsToPeople]
  );

  const handleDialogNewWhatsApp = useCallback(() => {
    setDialogNewWhatsApp((prev) => !prev);
  }, []);

  const invalidateWhatsAppInstances = useCallback(() => {
    queryClient.invalidateQueries("whatsAppInstances");
  }, []);

  return (
    <>
      <div className={styles.root}>
        <Typography variant="h4">WhatsApp</Typography>
      </div>
      <div className={styles.rootActions}>
        <Button onClick={handleDialogNewWhatsApp} variant="contained">
          Nova instância
        </Button>
        <Tooltip title="Atualizar lista" placement="top">
          <IconButton
            className={styles.btnPutList}
            onClick={invalidateWhatsAppInstances}
          >
            <Autorenew />
          </IconButton>
        </Tooltip>
      </div>
      <Paper variant="outlined" elevation={0} className={styles.paper}>
        <Table
          toolbar={false}
          data={whatsAppInstances.data as []}
          isLoading={whatsAppInstances.isFetching}
          columns={[
            { title: "#ID", field: "id" },
            { title: "Nome", field: "name" },
            {
              title: "Data de criação",
              field: "created_at",
              render: (row) =>
                format(
                  parseISO(row.created_at?.replace("Z", "")),
                  "dd/MM/yyyy HH:mm"
                ),
            },
            {
              title: "Ações",

              render: (row) => (
                <Stack direction="row" spacing={1}>
                  <Tooltip title="Detalhes" placement="top">
                    <IconButton
                      onClick={() => {
                        handleDialogDetailsWhatsApp(row);
                      }}
                    >
                      <Visibility />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="QR Code" placement="top">
                    <IconButton
                      onClick={() => {
                        handleDialogQrCodeWhatsApp(row);
                      }}
                    >
                      <QrCode />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Editar" placement="top">
                    <IconButton
                      onClick={() => {
                        handleDialogConfigWhatsApp(row);
                      }}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Excluir" placement="top">
                    <IconButton
                      onClick={() => {
                        handleDialogDeleteWhatsApp(row);
                      }}
                    >
                      <DeleteOutlineRounded />
                    </IconButton>
                  </Tooltip>
                  <Tooltip
                    title="Converter contatos em pessoas"
                    placement="top"
                  >
                    <IconButton
                      onClick={() => {
                        handleConvertContactsToPeople(row);
                      }}
                    >
                      <Autorenew />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ),
            },
          ]}
          style={{ backgroundColor: "transparent" }}
          headerStyle={{ backgroundColor: "transparent" }}
          options={{
            actionsColumnIndex: -1,
            pageSize: 5,
            selectionProps: () => ({
              color: "primary",
            }),
          }}
        />
      </Paper>
      <DetailsWhatsApp
        open={dialogDetailsWhatsApp}
        data={whatsAppInstance}
        onClose={() => {
          handleDialogDetailsWhatsApp(null);
        }}
      />
      <ConfigWhatsApp
        open={dialogConfigWhatsApp}
        data={whatsAppInstance}
        onClose={() => {
          handleDialogConfigWhatsApp(null);
        }}
      />
      <QrCodeWhatsApp
        open={dialogQrCodeWhatsApp}
        data={whatsAppInstance}
        onClose={() => {
          handleDialogQrCodeWhatsApp(null);
        }}
      />
      <NewWhatsApp open={dialogNewWhatsApp} onClose={handleDialogNewWhatsApp} />
    </>
  );
}
